import React, { FC, useCallback, useLayoutEffect, useState } from 'react';
import { ContentTag } from '@round/api';
import styles from './TagsWithPopover.module.css';
import Badge from '../../../../../../../../ui/Badge/Badge';
import { Popover } from '@round/ui-kit';
import cn from 'classnames';

const TagsWithPopover: FC<{ tags: ContentTag[] | undefined; containerClassName?: string }> = ({
    tags,
    containerClassName,
}) => {
    const [popoverRef, setPopoverRef] = useState<HTMLDivElement | null>(null);
    const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);

    const [shouldRenderPopover, setShouldRenderPopover] = useState(false);
    const [isNeedsPopover, setIsNeedsPopover] = useState(false);

    const [renderedTags, setRenderedTags] = useState<ContentTag[] | undefined>(tags);
    const [removedTags, setRemovedTags] = useState<ContentTag[]>([]);

    const checkIsOverflown = (wrapper: HTMLDivElement | null) => {
        if (wrapper) {
            return wrapper.scrollHeight > wrapper.clientHeight;
        }
    };

    const removeLast2Tags = useCallback(() => {
        if (!renderedTags) {
            return;
        }
        const newTags = [...renderedTags];
        if (!newTags) return;
        const removedTags = newTags.splice(newTags.length - 2, 2);

        setRenderedTags(newTags);
        if (removedTags.filter((tag): tag is ContentTag => tag !== undefined)) {
            setRemovedTags((prev) => [...prev, ...removedTags]);
        }
    }, [renderedTags]);

    // eslint-disable-next-line
    useLayoutEffect(() => {
        if (checkIsOverflown(wrapperRef)) {
            removeLast2Tags();
            setIsNeedsPopover(true);
        } else {
            if (removedTags.length) {
                setShouldRenderPopover(true);
            }
        }
    });

    useLayoutEffect(() => {
        function handleResize() {
            setRenderedTags(tags);
            setRemovedTags([]);
            setShouldRenderPopover(false);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });

    return (
        <div ref={setWrapperRef} className={styles.wrapper}>
            <div className={cn(styles.container, containerClassName)}>
                {renderedTags?.map((tag) => (
                    <Badge key={tag.id} className={styles.tag}>
                        <div>{tag.name}</div>
                    </Badge>
                ))}
                {isNeedsPopover && shouldRenderPopover && (
                    <div ref={setPopoverRef}>
                        <Badge className={styles.tag}>
                            <div>{`& ${removedTags.length} more`}</div>
                        </Badge>
                        <Popover
                            anchorElement={popoverRef}
                            showOn="hover"
                            options={{
                                placement: 'bottom-end',
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, 5],
                                        },
                                    },
                                ],
                            }}
                        >
                            <div className={cn(styles.container, styles.popover, styles.show)}>
                                {removedTags?.map((tag) => (
                                    <Badge key={tag.id} className={styles.tag}>
                                        <div>{tag.name}</div>
                                    </Badge>
                                ))}
                            </div>
                        </Popover>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TagsWithPopover;
